import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "../../firebase-config";
import { toast } from "react-toastify";
import { setNotificationPermission } from "../../redux/reducers/notificationSlice";
import { last_seen } from "../../data/ApiEndpoints";
import { commonPostPutApi1 } from "../../redux/reducers/commonApiSlice";
import OdmLeadAlerts from "./OdmLeadAlerts";

const Notifications = ({ authData }) => {
  const dispatch = useDispatch();

  const [isTokenGenerated, setIsTokenGenerated] = useState(null);

  const wsRef = useRef(null);
  const wsBackoffRef = useRef(2000);
  const wsBcRef = useRef(null);

  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

  const ODM_ALERT_WHITELIST = useMemo(
    () => [
      "tinkuchhabra.assetdeals@gmail.com",
      "deepti.chauhan@wtfgyms.com",
      "chetan.sharma@wtfgyms.com",
      "soma@wtfgyms.com",
      "danishansari2088288@gmail.com",
      "tomarshaily012@gmail.com",
      "thakurmayank5345@gmail.com",
      "aditichamyal594@gmail.com",
      // "naresh.kukreti@wtfup.me",
    ],
    []
  );

  const isUserWhitelisted = useCallback(
    (user) => {
      if (!user) return false;
      const userEmail = user.email || user.user_email || "";
      if (!userEmail) return false;

      return ODM_ALERT_WHITELIST.includes(userEmail.toLowerCase());
    },
    [ODM_ALERT_WHITELIST]
  );

  const getDeviceId = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const { ip } = data;
      return ip;
    } catch (error) {
      return "";
    }
  };

  const requestPermission = async () => {
    setIsTokenGenerated(null);

    if (
      typeof Notification !== "undefined" &&
      !/iPad|iPhone|iPod/.test(navigator.userAgent)
    ) {
      try {
        const permission = await Notification.requestPermission();

        dispatch(setNotificationPermission(permission));

        if (permission === "granted") {
          try {
            const token = await getToken(messaging, {
              vapidKey:
                "BH3evanDRc1c88D-4GmIEnZdabhWjya5WEX767qJFzGN1F7_0jR6VeF8QfGNzg6KxK9b-y-wOcpxbyhRSmhB008",
            });
            if (token) {
              setIsTokenGenerated(token);
            } else {
            }
          } catch (error) {}
        } else {
        }
      } catch (err) {}
    } else {
    }
  };

  const showNotification = (title, options) => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => {
          registration.showNotification(title, options);
        })
        .catch((error) => {});
    } else {
      toast.error("This browser does not support Service Workers.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        progress: undefined,
      });
    }
  };

  const handleGenerateToken = async (requestBody) => {
    const method = "POST";
    const endpoint = last_seen;
    const payload = requestBody;
    const token = authData?.token;

    try {
      const result = await dispatch(
        commonPostPutApi1({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
      } else {
        const errorMessage = result?.error?.message || result?.payload?.message;
      }
    } catch (error) {}
  };

  const handleTokenGeneration = async (isTokenGenerated) => {
    try {
      const deviceId = await getDeviceId();

      const requestBody = {
        user_id: authData?.user_id || authData?.uid || "",
        device_id: deviceId,
        n_token: isTokenGenerated,
      };

      await handleGenerateToken(requestBody);
    } catch (error) {}
  };

  useEffect(() => {
    if (isTokenGenerated) {
      handleTokenGeneration(isTokenGenerated);
    }
  }, [isTokenGenerated, authData]);

  useEffect(() => {
    if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      setIsTokenGenerated(null);

      try {
        onMessage(messaging, (payload) => {
          toast.info(
            `${payload.notification.title}: ${payload.notification.body}`,
            {
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: false,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            }
          );

          showNotification(payload.notification.title, {
            body: payload.notification.body,
            icon: payload.notification.icon || "/default-icon.png",
            click_action:
              payload.notification.click_action || "https://your-website.com",
          });
        });

        requestPermission();
      } catch (error) {
        toast.error(
          "An error occurred while setting up notifications. Please check your browser settings."
        );
      }
    } else {
      toast.warn("Notifications are not supported on this browser or device.");
    }
  }, []);

  useEffect(() => {
    const broadcastChannel = new BroadcastChannel("firebase-notifications");

    broadcastChannel.onmessage = (event) => {
      const { title, body } = event.data.notification;

      toast.info(`${title}: ${body}`, {
        position: toast.POSITION.BOTTOM_RIGHT,
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    };

    return () => {
      broadcastChannel.close();
    };
  }, []);

  useEffect(() => {
    const WS_URL = process.env.REACT_APP_ODM_WSS_URL;
    const acc = authData?.account_type;

    // Skip WebSocket connection for receptionist accounts
    if (!WS_URL || !authData || isIOS() || acc === "receptionist") return;

    let stopped = false;
    try {
      wsBcRef.current = new BroadcastChannel("odm-alerts-events");
    } catch {
      wsBcRef.current = null;
    }

    const computeChannels = () => {
      try {
        const acc = authData?.account_type;
        const channels = [];

        if (acc === "admin") {
          if (!isUserWhitelisted(authData)) {
            return [];
          }
          channels.push("global:odm");
        }

        // if (acc === "receptionist" && authData?.gym_id) {
        //   channels.push(`gym:${authData.gym_id}`);
        // }
        if (acc === "cluster_manager") {
          const userGyms = authData?.gyms?.gyms || authData?.gyms || [];
          if (Array.isArray(userGyms)) {
            userGyms.forEach((g) => {
              const gid = g?.value || g?.gym_id || g?.uid || g;
              if (gid) channels.push(`gym:${gid}`);
            });
          }
        }

        return Array.from(new Set(channels));
      } catch {
        return [];
      }
    };

    const connect = () => {
      if (stopped) return;
      try {
        const token = encodeURIComponent(authData?.token || "");
        const ws = new WebSocket(`${WS_URL}?token=${token}`);
        wsRef.current = ws;

        ws.onopen = () => {
          wsBackoffRef.current = 2000;
          const channels = computeChannels();

          try {
            ws.send(JSON.stringify({ type: "SUBSCRIBE", channels: channels }));
          } catch (e) {}
        };

        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);

            if (
              msg?.event_type === "new_membership" ||
              msg?.event_type === "bca_pending" ||
              msg?.event_type === "pt_trial_pending" ||
              msg?.event_type === "member_success_checkin" ||
              msg?.event_type === "renewal_member_checkin" ||
              msg?.event_type === "member_success_checkin"
            ) {
              return;
            }

            if (
              msg?.event_type === "new_odm_added" &&
              msg?.status &&
              msg?.data
            ) {
              if (wsBcRef.current) {
                wsBcRef.current.postMessage(msg);
              } else {
              }
            }
            // else if (
            //   msg?.event_type === "new_membership" &&
            //   msg?.status &&
            //   msg?.data
            // ) {
            //   const membershipBc = new BroadcastChannel("membership-events");
            //   membershipBc.postMessage(msg);
            //   membershipBc.close();
            // }
            else {
            }
          } catch (err) {}
        };

        ws.onclose = () => {
          if (stopped) return;
          try {
          } catch {}
          const delay = Math.min(wsBackoffRef.current, 30000);
          setTimeout(connect, delay);
          wsBackoffRef.current = Math.min(wsBackoffRef.current * 2, 30000);
        };

        ws.onerror = () => {
          try {
          } catch {}
          try {
            ws.close();
          } catch {}
        };
      } catch {
        const delay = Math.min(wsBackoffRef.current, 30000);
        setTimeout(connect, delay);
        wsBackoffRef.current = Math.min(wsBackoffRef.current * 2, 30000);
      }
    };

    connect();

    return () => {
      stopped = true;
      try {
        if (wsRef.current && wsRef.current.readyState === 1)
          wsRef.current.close();
      } catch {}
      try {
        if (wsBcRef.current) wsBcRef.current.close();
      } catch {}
    };
  }, [authData, isUserWhitelisted]);

  // Don't render ODM alerts for receptionist accounts
  if (authData?.account_type === "receptionist") {
    return null;
  }

  return <OdmLeadAlerts />;
};

export default Notifications;
